import { SKILL_TYPE, SKILL_ORIGIN } from '@/constants'

export interface PlanFeature {
  name: string
  modal?: string
}

export interface Plan {
  tagline: string
  duration: number
  features: Array<PlanFeature>
  price: number
  stripeDiscount: number
  basePrice?: number
  savings?: string
  hasStripePlan?: boolean
  pricing?: string
  isPromoted?: boolean
  hasTrial?: boolean
  trialDays: number
  trialDurationText?: string
  heading?: string | null
  subHeading?: string | null
  extraBullet?: string | null
  planName?: string
  isConsumerPlan?: boolean
  displayName?: string
  promoDisplay?: string
  billValue?: string
}
export interface ApiResponse {
  data: any
  error: any
  meta?: any
}
export interface Country {
  country: string
  code: string
  alpha_2_code: string
  [propName: string]: any
}
export interface Region {
  region: string
  code: string
  country_code: string
  [propName: string]: any
}

export interface Toaster {
  id: number
  name: string
  content: string
  user_group: {
    userGroup: string[]
  }
  is_enable: number
  priority: number
  expires_at: string | null
  created_at: string
  updated_at: string
}

export interface FlashMessage {
  headerMessage: {
    type: string
    dismissible: boolean
    message: string
  }
}

export interface Notification {
  kind: string
  name: string
  type?: string
  content: string
  canUserDismiss: boolean
}
export interface PasswordResetDto {
  email?: string
  token?: string
  oldPassword?: string
  password: string
  passwordConfirmed: string
}

export interface LogInDto {
  email: string
  password: string
}

export interface RegisterDto {
  email: string
  password: string
}

export enum MessageTypes {
  INFO = 'info',
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
  PROMO = 'promo',
  ANNOUNCEMENT = 'announcement',
  ACTION = 'action',
}

export interface Profile {
  email?: string
  firstName?: string
  lastName?: string
  zip?: string
  city?: string
  state?: string
  country?: string
  phoneNumber?: string
  role?: string
  subRole?: string
  countrySearch?: string
  stateSearch?: string
  citySearch?: string
  zipSearch?: string
  radiusSearch?: string
  jobTypeSearch?: string
  birthDate?: string
  seekingSince?: string
  goal?: string
  educationInfo?: any
  allowEmail?: boolean
  industry?: string
  excludedSkills?: string[]
  socialAccounts?: any[]
  emailVerified?: boolean
  domain?: any
  demographics?: any
  clientQuota?: number
  clientCount?: number
  invitationCount?: number
  planExpiration?: string
  canAccessClientReport?: boolean
  allowCalendarInterviewsEmail?: boolean
  reportDomains?: any[]
}

export interface FieldSchema {
  name: string
  label?: string
  type: string
  id?: string
  width?: number
  initialValue?: any
  placeholder?: string
  options?: any[]
  validationRule: any
  autocomplete?: boolean
  disabled?: boolean
  readonly?: boolean
  required?: boolean
  success?: boolean
  inputAttrs?: any
  [additional: string]: any
}
export interface FormSchema {
  inputs: FieldSchema[]
  action: (values: any) => Promise<any>
}

export type OptionType = {
  name: string
  value: any
  selected?: boolean
}

export type Subscription = {
  id: number
  status: string
  plan: string
  promo: string
  endsAt?: string
  trialEndsAt?: string
  createdAt?: string
  updatedAt?: string
  periodEnd?: string
  periodStart?: string
  nextPaymentAttempt?: string
  subtotal?: number
  tax?: number
  total?: number
  [additional: string]: any
}

export interface FeedbackDto {
  get_interview?: string | null // Yes, No
  get_job?: string | null // Yes, No
  get_reason?: string
  feedback?: string
  email?: string
  created_at?: string // Subscription start date
}
export interface PaymentMethod {
  brand: string
  exp_month: number
  exp_year: number
  last4: string
}
export interface Scan {
  metadata: any
  isStarred: boolean
  findings: Finding[]
  highValueSkills?: Skill[]
  matchRate: MatchRate
  skills?: {
    hard: Skill[]
    soft: Skill[]
    other: Skill[]
  }
  keywords?: Keyword[]
  coverLetter?: string
  resume: string
  jobDescription: string
  delta?: any
  relatedScans?: any[]
  history?: number[]
  meta?: any
  rawResumeText?: string
  excludedSkills?: {
    hard?: Skill[]
    soft?: Skill[]
    other?: Skill[]
  }
}

export interface ScanMeta {
  title: string
}

export interface Finding {
  key: string
  name: string
  status: string
  category: FindingCategory
  subCategory: string
  statusReason: string | null
  documentType: string
  subcategory?: string | null
  evidence?: FindingEvidence | null
  variables: any[]
  description?: string
  message?: string
  title?: string
  checks?: any[]
}

export enum FindingCategory {
  'SEARCHABILITY' = 'Searchability',
  'CONTACT_INFO' = 'Contact Information',
  'RECRUITER_TIPS' = 'Recruiter Tips',
  'FORMATTING' = 'Formatting',
  'SKILLS' = 'Skills',
  'COVERLETTER' = 'Coverletter',
  'JOB_DETAILS' = 'Job Details',
}

export enum ScoreSections {
  SEARCHABILITY = 'searchability',
  HARD_SKILLS = 'hardSkills',
  SOFT_SKILLS = 'softSkills',
  OTHER_SKILLS = 'otherSkills',
  RECRUITER_TIPS = 'recruiterTips',
  FORMATTING = 'formatting',
  COVER_LETTER = 'coverletter',
}

export enum FindingSubcategory {
  headline = 'headline',
  profileSummary = 'profileSummary',
  vanityUrl = 'vanityUrl',
  sectionHeadings = 'sectionHeadings',
  fileType = 'fileType',
  fontCheck = 'fontCheck',
  pageSetup = 'pageSetup',
  layout = 'layout',
}

export enum FindingStatus {
  PASS = 'pass',
  WARN = 'warn',
  FAIL = 'fail',
}
export interface FindingEvidence {
  title: string
  items: string[]
}
export interface MatchRate {
  score: number
  searchability: MatchMetric
  skills: MatchMetric
  recruiterTips: MatchMetric
  formatting: MatchMetric
  coverLetter: MatchMetric
}
export interface MatchMetric {
  score: number
  issueCount: number
}

export interface Keyword {
  name: string
  counts: SkillCount
  scope: 'global' | 'personal'
  type: 'included' | 'excluded'
}

export type SkillType =
  | typeof SKILL_TYPE.HARD
  | typeof SKILL_TYPE.SOFT
  | typeof SKILL_TYPE.OTHER

export type SkillOrigin =
  | typeof SKILL_ORIGIN.ORIGINAL
  | typeof SKILL_ORIGIN.PREDICTED
  | typeof SKILL_ORIGIN.VARIATION

export interface LegacySkill {
  name: string
  type: SkillType
  counts: SkillCount
  id?: string
  isRequired?: boolean
  isPredicted?: boolean
  variations?: Variation[]
  phrases: string[]
  isObfuscated?: boolean
}

export interface Skill {
  type: string
  unstemmedJdCount: number
  resumeCount: number
  jdCount: number
  exactMatches: string[]
  aliasMatches: string[]
  name: string
  required: boolean
  aliases: string[]
  categories: string[]
  stemmed: string
  predicted: boolean
  isObfuscated?: boolean
}
export interface SkillCount {
  resumeMatches?: number
  coverLetterMatches?: number
  jobDescriptionMatches: number
}
export interface Variation {
  text: string
  id?: string
  count?: number
}

export enum ScanType {
  resume,
  linkedin,
}

export enum UserType {
  GUEST = 'guest',
  FREE = 'free',
  PREMIUM = 'premium',
  COACH = 'coach',
  DOMAIN = 'domain',
}

export interface SectionType {
  name: string
  href?: string
  route?: string
  isPremium?: boolean
}

export interface ReportSkillDto {
  shouldExclude: boolean
  reason?: string
  feedback?: string
  skill?: string
  skillId?: string
  skillType?: string
  excludableType?: string
}

export interface ReportSkillDto {
  shouldExclude: boolean
  reason?: string
  feedback?: string
  skill?: string
  skillId?: string
  skillType?: string
  excludableType?: string
}

export interface ExcludeSkillDto {
  reason?: string
  feedback?: string
  skill?: string
  excludableType?: string
  excludableId?: number
}

export interface Persona {
  id: string
  email: string
}

export interface JobTitle {
  id: string
  name: string
}

export interface CustomSkill {
  name: string
  type: SkillType
}

// TODO: Complete these placeholders

export type EducationLevel =
  | 'none'
  | 'highschool'
  | 'certification'
  | 'associates'
  | 'bachelors'
  | 'masters'
  | 'doctorate'
  | 'professor'
  | 'law'
  | 'doctor'

export type JobDescriptionEducationLevel =
  | 'None'
  | 'High school graduate'
  | 'Associates degree'
  | 'Bachelors degree'
  | 'Masters degree'
  | 'Professional degree'
  | 'Doctorate degree'

export interface JD {
  id?: number
  title: string
  companyName: string
  content: string
  url: string
  scans?: any[]
  education_manual?: EducationLevel
}

export interface Resume {
  id: number
  [key: string]: any
}

export interface JobDescription {
  id: number
  name: string
  data: any
  rawContent: string
  source: string
  url: string
}
export interface CV {
  id?: number
  name?: string
  content: string
}
export interface Note {
  id?: number
  content: string
  createdAt?: string
  updatedAt?: string
}

export interface Contact {
  id: number
  firstName: string
  lastName: string
  title: string
  phone: string
  email: string
  url: string
  note: string
  createdAt: string
  updatedAt: string
}

export enum InterviewType {
  PHONE = 'phone',
  VIDEO = 'video',
  IN_PERSON = 'in_person',
}

export interface InterviewContact {
  name: string
  email: string
  phone: string
  url: string
  address: string
}
export interface Interview {
  id?: number
  type: InterviewType
  opportunity_id?: number
  name?: string
  description?: string
  when: string
  contact?: InterviewContact
}

export interface OpportunityDTO {
  id?: number
  title?: string
  stage?: TrackerStage
  description?: string
  jobTitle?: string
  company?: string
  offer?: string
  salary?: string | null
  location?: string
  url?: string
  deadline?: string
  application?: string | null
  cv_id?: number
  resume_id?: number
  sort?: number
  archived?: boolean
  education_manual?: JobDescriptionEducationLevel
  skill_manual?: CustomSkill
  job_description_id?: number | null
}

export interface Opportunity {
  id: number
  title?: string
  stage?: TrackerStage
  score?: number
  submittedCv: CV
  jd: JD
  resume: any
  jobDescription: JobDescription
  lastScanScore?: number
  lastScanId?: number
  description?: string
  jobTitle: string
  company?: string
  offer?: string
  salary?: string
  companyName?: string
  location?: string
  url?: string
  deadline?: string
  application?: string
  notes: Note[]
  interviews: Interview[]
  coverletterId?: number | null
  contacts?: Contact[]
  createdAt?: string
  updatedAt?: string
  archived: boolean
  reportMode: string
}

export enum TrackerStage {
  SAVED = 'saved',
  APPLIED = 'applied',
  INTERVIEW = 'interview',
  OFFER = 'offer',
  REJECTED = 'rejected',
}

export enum ResumeState {
  SOURCE = 'source',
  MODIFIED = 'modified',
  OPTIMIZED = 'optimized',
}

export interface OpportunityPosition {
  id: number
  index: number
}

export type AnalyticsItemValue = {
  new_clients: number
  new_coaches: number
  total_clients: number
  total_coaches: number
}

export interface DomainStatistics {
  id: number
  coachSeatsTotal: number
  coachSeatsUsed: number
  premiumSeatsTotal: number
  premiumSeatsUsed: number
  domainQuotaReserved: number
  downgradedSeats: number
  scansTotal: number
  contractStart: string
  contractRenewal: string
}

export interface AnalyticsItem {
  [month: string]: AnalyticsItemValue
}

export interface AddDomainUser {
  email: string
  firstName?: string
  lastName?: string
}

export interface DeactivateCoaches {
  email: string[]
  newCoachId?: number
}
export interface TabItem {
  name: string
  icon?: string
  count?: number
}

export type ResumeTheme =
  | 'default'
  | 'bluey'
  | 'plurple'
  | 'greenish'
  | 'wellred'
  | 'realteal'
  | 'yeller'

export interface SnackBar {
  show: boolean
  closeable?: boolean
  title?: string
  message?: string
  icon?: SnackBarIcon
  action?: SnackBarAction
  link?: SnackBarLink
  position?: 'bottom-center' | 'bottom-left'
}

export interface SnackBarLink {
  text: string
  to: string
}
export interface SnackBarAction {
  text: string
  action: () => void
}
export interface SnackBarIcon {
  color: string
  icon: Array<string>
}

export interface Job {
  id?: number
  name: string
  data: any
  rawContent: string
  parser: string
  source: string
  url: string
  createdAt: string
  updatedAt: string
  jobPostingDate?: string
  opportunity?: any
}

export interface FeedJob {
  id?: number
  jobDescriptionId?: number
  savedOpportunityId?: number
  name: string
  data: any
  rawContent: string
  parser: string
  source: string
  url: string
  createdAt: string
  updatedAt: string
  jobPostingDate?: string
}

export interface JobSearchOptions {
  source?: string | null
  searchString?: string | null
  searchLocation?: string | null
  addressObject?: AddressObject | null
  workType?: string | null
  workLocation?: string | null
  sortBy?: string | null
  sortDir?: string | null
  perPage?: number | null
  page?: number | null
  postingDate?: string | null
  distance?: string | null
  useServerless?: boolean | null
}

export interface PageInfo {
  current_page: number
  last_page: number
  per_page: number
  total: number
  [additional: string]: any
}

export interface AddressObject {
  formattedAddress?: string
  latitude: number
  longitude: number
  city?: string
  state?: string
  county?: string
  postalCode?: string
  country?: string
}

// Extend the window object
declare global {
  interface Window {
    Quill: any
    quillInstance: any
    churnkey: any
    posthog: any
    rudderanalytics: any
  }
}

export enum Permissions {
  // ATS
  CREATE_ATS = 'create ats',
  READ_ATS = 'read ats',
  UPDATE_ATS = 'update ats',
  DELETE_ATS = 'delete ats',
  // CVS
  CREATE_CVS = 'create cvs',
  READ_CVS = 'read cvs',
  UPDATE_CVS = 'update cvs',
  DELETE_CVS = 'delete cvs',
  // JDS
  CREATE_JDS = 'create jds',
  READ_JDS = 'read jds',
  UPDATE_JDS = 'update jds',
  DELETE_JDS = 'delete jds',
  // SCANS
  CREATE_SCANS = 'create scans',
  READ_SCANS = 'read scans',
  UPDATE_SCANS = 'update scans',
  DELETE_SCANS = 'delete scans',
  // USERS
  CREATE_USERS = 'create users',
  READ_USERS = 'read users',
  UPDATE_USERS = 'update users',
  DELETE_USERS = 'delete users',
  // DOMAIN
  CREATE_DOMAIN = 'create domain',
  READ_DOMAIN = 'read domain',
  UPDATE_DOMAIN = 'update domain',
  DELETE_DOMAIN = 'delete domain',
  // DOMAIN ADMIN
  CREATE_DOMAIN_ADMIN = 'create domain admin',
  READ_DOMAIN_ADMIN = 'read domain admin',
  UPDATE_DOMAIN_ADMIN = 'update domain admin',
  DELETE_DOMAIN_ADMIN = 'delete domain admin',
  VIEW_ANY_DOMAIN_REPORT = 'view any domain report',
  // COACHES
  CREATE_COACH = 'create coach',
  READ_COACH = 'read coach',
  UPDATE_COACH = 'update coach',
  DELETE_COACH = 'delete coach',
  DOWNGRADE_COACH = 'downgrade coach',
  INVITE_COACH = 'invite coach',
  REVOKE_COACH_INVITATION = 'revoke coach invitation',
  // OWN CLIENTS
  INVITE_CLIENT = 'invite client',
  REVOKE_OWN_CLIENT_INVITATION = 'revoke own client invitation',
  DOWNGRADE_OWN_CLIENT = 'downgrade own client',
  CREATE_CLIENT = 'create client',
  READ_OWN_CLIENT = 'read own client',
  UPDATE_OWN_CLIENT = 'update own client',
  DELETE_OWN_CLIENT = 'delete own client',
  // ANY CLIENTS
  REVOKE_ANY_CLIENT_INVITATION = 'revoke any client invitation',
  DOWNGRADE_ANY_CLIENT = 'downgrade any client',
  UPDATE_ANY_CLIENT = 'update any client',
  DELETE_ANY_CLIENT = 'delete any client',
  READ_ANY_CLIENT = 'read any client',
  ASSIGN_CLIENT = 'assign client',
  UNASSIGN_CLIENT = 'unassign client',
  // KEYWORDS
  CREATE_KEYWORDS = 'create keywords',
  READ_KEYWORDS = 'read keywords',
  UPDATE_KEYWORDS = 'update keywords',
  DELETE_KEYWORDS = 'delete keywords',
  // SKILLS
  CREATE_SKILLS = 'create skills',
  READ_SKILLS = 'read skills',
  UPDATE_SKILLS = 'update skills',
  DELETE_SKILLS = 'delete skills',
  MATCH_SKILLS = 'match skills',
  // SKILL_PHRASES
  CREATE_SKILL_PHRASES = 'create skill phrases',
  READ_SKILL_PHRASES = 'read skill phrases',
  UPDATE_SKILL_PHRASES = 'update skill phrases',
  DELETE_SKILL_PHRASES = 'delete skill phrases',
  IMPORT_SKILL_PHRASES = 'import skill phrases',
  // FEATURES
  ACCESS_RESULTS_2_0 = 'access results 2.0',
  SCAN_COVER_LETTER = 'scan cover letter',
  SCAN_LINKEDIN = 'scan linkedin',
  USE_POWEREDIT = 'use poweredit',
}

export enum UserRoles {
  SUPER_ADMIN = 'Super-Admin',
  INTERNAL_USER_ADMIN = 'Internal User Admin',
  INTERNAL_REPORTING_ADMIN = 'Internal Reporting Admin',
  INTERNAL_DOMAIN_ADMIN = 'Internal Domain Admin',
  INTERNAL_PRODUCT_DATA_ADMIN = 'Internal Product Data Admin',
  INTERNAL_SUPPORT = 'Internal Support',
  B2B_GRANDFATHERED_2023 = 'B2B Grandfathered 2023',
  B2B_BASE = 'B2B Base',
  DOMAIN_ADMIN = 'Domain Admin',
  COACH_GLOBAL = 'Coach Global',
  COACH = 'Coach',
  COACH_PREMIUM = 'Coach Premium',
  CLIENT = 'Client',
  SUBSCRIBER = 'Subscriber',
  PAID = 'Paid',
  BETA_TESTER = 'Beta Tester',
  WIDGET_PARTNER = 'Widget Partner',
}

export enum UserFlags {
  // Email Flags
  DISABLE_EMAIL_SCAN = 'disable_email_scan',
  DISABLE_EMAIL_APPLICATION = 'disable_email_application',
  DISABLE_EMAIL_INTERVIEW = 'disable_email_interview',
  DISABLE_EMAIL_MARKETING = 'disable_email_marketing',

  // Power Edit 3
  DISABLE_POWER_EDIT_3 = 'disable_power_edit_3',
  POWER_EDIT_3_AI = 'power_edit_3_ai',
  POWER_EDIT_3_DOCX = 'power_edit_3_docx',
  MATCH_REPORT_DEFAULT = 'match_report_default',

  // Onboarding Flags
  ONBOARDING = 'onboarding',
  ONBOARDING_RESUME_CREATED = 'onboarding_resume_created',
  ONBOARDING_JD_CREATED = 'onboarding_jd_created',
  ONBOARDING_SCAN_REPORT = 'onboarding_scan_report',
  ONBOARDING_PROGRESS_TRACKED = 'onboarding_progress_tracked',
}

export type FeatureFlags = {
  [flag: string]: boolean
}

export interface RephrasePopup {
  isOpen: boolean
  targetPath?: string
  coords: {
    top: number
    left: number
    right: number
    bottom: number
  }
}

export interface RephraseParams {
  originalPhrase: string
  type: 'highlight' | 'summary'
  opportunityId: number
  skills?: string[]
}
export interface LioTableHeader {
  text: string
  value: string
  tooltip?: string
  type: 'text' | 'boolean'
}

export interface LioCheck {
  name?: string
  nameTooltip?: string
  itemTooltipTitle?: string
  itemTooltip?: string
  successful?: boolean
  value: string
  isUserData?: boolean
  hasEvidence?: boolean
  evidenceTitle?: string
  evidences?: any
}

export interface LinkedinReportPayload {
  jobs: number[]
  linkedin_profile_url: string
}

export interface LioFinding {
  category: string
  documentType: string
  evidence: {
    items: any
    title?: string
  }
  key: string
  status: string
  statusReason: string
  subCategory: string
  variables: any[]
}

export interface LioEducation {
  schoolName: string
  findings: LioFinding[]
}

export interface LioPosition {
  company: string
  findings: LioFinding[]
}

export interface Checks {
  num_fails: number
  num_warns: number
  num_passes: number
  num_skips: number
}

export interface LinkedinReport {
  checks: Checks
  education: LioEducation[]
  findings: LioFinding[]
  positions: LioPosition[]
  score: number
  headline: string
  summary: string
}

export interface LinkedinRecipientInfo {
  company: string
  firstName: string
  headline: string
  lastName: string
  role: string
  profileId: string
  profilePicture: string
}

export interface StagesCount {
  saved: number
  applied: number
  interview: number
  offer: number
}
